import React, { FC, Fragment, useCallback } from 'react'
import { InView } from 'react-intersection-observer'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import {
  DiscoverItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { LinkViewModel } from '@/services/RenderService'
import { WatchlistItem } from '@/services/WatchlistService'

export interface WatchlistCardProps {
  watchlistItem: WatchlistItem
  linkModel: LinkViewModel
  itemIndex: number
  title?: string
  hoverStartTimeRef: React.MutableRefObject<Map<string, number>>
  impressionTimeoutRef: React.MutableRefObject<Map<string, NodeJS.Timeout>>
  hasTrackedImpression: React.MutableRefObject<Set<string>>
  createInteraction: (watchlistItem: WatchlistItem, itemIndex: number) => DiscoverItemInteraction | null
}

export const WatchlistCard: FC<WatchlistCardProps> = ({
  watchlistItem,
  linkModel,
  itemIndex,
  title,
  hoverStartTimeRef,
  impressionTimeoutRef,
  hasTrackedImpression,
  createInteraction,
}) => {
  const { trackInteraction } = useContentInteraction()
  const contentId = watchlistItem?.id ?? ''

  const handleMouseEnter = useCallback(() => {
    if (!contentId) return
    hoverStartTimeRef.current.set(contentId, Date.now())
  }, [contentId, hoverStartTimeRef])

  const handleMouseLeave = useCallback(() => {
    if (!contentId) return

    const startTime = hoverStartTimeRef.current.get(contentId)
    if (!startTime) return

    const dwellTime = Date.now() - startTime

    if (dwellTime >= HOVER_THRESHOLD_MS) {
      const interaction = createInteraction(watchlistItem, itemIndex)
      if (interaction) {
        trackInteraction(interaction.withHover(dwellTime))
      }
    }

    hoverStartTimeRef.current.delete(contentId)
  }, [contentId, hoverStartTimeRef, createInteraction, watchlistItem, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    const interaction = createInteraction(watchlistItem, itemIndex)
    if (interaction) {
      trackInteraction(interaction.withPress())
    }
  }, [createInteraction, watchlistItem, itemIndex, trackInteraction])

  const handleInViewChange = useCallback(
    (inView: boolean) => {
      if (!contentId) return

      if (inView) {
        const timeout = setTimeout(() => {
          if (hasTrackedImpression.current.has(contentId)) return
          hasTrackedImpression.current.add(contentId)

          const interaction = createInteraction(watchlistItem, itemIndex)
          if (interaction) {
            trackInteraction(interaction.withImpression())
          }
        }, 1000)

        impressionTimeoutRef.current.set(contentId, timeout)
      } else {
        const timeout = impressionTimeoutRef.current.get(contentId)
        if (timeout) {
          clearTimeout(timeout)
          impressionTimeoutRef.current.delete(contentId)
        }
      }
    },
    [
      contentId,
      hasTrackedImpression,
      createInteraction,
      watchlistItem,
      itemIndex,
      trackInteraction,
      impressionTimeoutRef,
    ],
  )

  return (
    <Fragment key={watchlistItem?.id}>
      <InView onChange={handleInViewChange}>
        <LandscapeTileCard
          className="mb-2 mr-4"
          linkModel={linkModel}
          key={watchlistItem?.id}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
        <TitleAndSubtitleDisplay className="mr-4" title={title} subtitle={watchlistItem?.name ?? ''} />
      </InView>
    </Fragment>
  )
}
